import { useEffect, useState } from 'react';
import {
  getMemberFinancialAccount,
  memberPaymentReceiptUrl,
  memberOfficialPaymentReceiptUrl,
  manualAllocationEvidenceUrl,
  DmStagingApiError,
  type MemberFinancialAccount as Account,
} from '@urbanik/api-client';
import {
  Button,
  CanonicalMetricGrid,
  CanonicalMetricCard,
  DataTable,
  EmptyState,
  MoneyValue,
  StatePanel,
  StatusBadge,
  canonicalStates,
  type DataColumn,
} from '@urbanik/ui';
import { CircleDollarSign, CreditCard, Eye, ListChecks, WalletCards } from 'lucide-react';
import { apiBase } from './api-base';
import { PaymentApplicationDetailModal } from './PaymentApplicationDetailModal';

export function MemberFinancialAccount({
  memberId,
  mode = 'account',
}: {
  memberId: string;
  mode?: 'account' | 'payments';
}) {
  const [data, setData] = useState<Account | null>(null),
    [error, setError] = useState(''),
    [refreshKey, setRefreshKey] = useState(0),
    [selectedPayment, setSelectedPayment] = useState<Account['payments'][number] | null>(null);
  useEffect(() => {
    const c = new AbortController();
    setError('');
    void getMemberFinancialAccount(apiBase, memberId, c.signal)
      .then(setData)
      .catch((problem) => {
        if (c.signal.aborted) return;
        if (problem instanceof DmStagingApiError && problem.kind === 'unauthorized') {
          window.location.hash = '#login';
          return;
        }
        setError(
          problem instanceof Error ? problem.message : 'No pudimos consultar el estado de cuenta.',
        );
      });
    return () => c.abort();
  }, [memberId, refreshKey]);
  if (error)
    return (
      <div className="ui-alert ui-alert--error" role="alert">
        <strong>No pudimos cargar el estado de cuenta</strong>
        <p>{error}</p>
      </div>
    );
  if (!data) return <StatePanel state={canonicalStates.find((s) => s.kind === 'loading')!} />;
  const paymentColumns: readonly DataColumn<Account['payments'][number]>[] = [
    { id: 'slot', header: 'Alícuota', accessor: (r) => r.slotNumber },
    { id: 'period', header: 'Período', accessor: (r) => r.period },
    {
      id: 'amount',
      header: 'Pago registrado',
      accessor: (r) => r.amount,
      cell: (r) => <MoneyValue value={r.amount} tone="positive" />,
    },
    {
      id: 'applied',
      header: 'Aplicado',
      accessor: (r) => r.appliedAmount,
      cell: (r) => <MoneyValue value={r.appliedAmount} />,
    },
    {
      id: 'credit',
      header: 'Crédito',
      accessor: (r) => r.credit,
      cell: (r) => <MoneyValue value={r.credit} />,
    },
    {
      id: 'status',
      header: 'Estado',
      accessor: (r) => r.status,
      cell: (r) => (
        <StatusBadge
          tone={
            r.status === 'reversed'
              ? 'danger'
              : r.status === 'allocated' || r.status === 'applied'
                ? 'success'
                : 'warning'
          }
        >
          {r.status === 'reversed'
            ? 'Reversado'
            : r.status === 'allocated' || r.status === 'applied'
              ? 'Aplicado'
              : 'Pendiente de aplicación'}
        </StatusBadge>
      ),
    },
    {
      id: 'evidence',
      header: 'Detalle',
      accessor: (r) => r.sourceReference,
      cell: (r) => (
        <Button icon={<Eye aria-hidden="true" />} onClick={() => setSelectedPayment(r)}>
          Ver detalle
        </Button>
      ),
    },
  ];
  if (mode === 'payments')
    return (
      <section role="tabpanel" className="member-payment-history">
        <header className="member-payment-history__heading">
          <div>
            <span>PAGOS HISTÓRICOS</span>
            <h3>Pagos, evidencia y comprobantes</h3>
            <p>
              Abre Ver detalle para consultar la evidencia original, la trazabilidad y las cuotas
              aplicadas.
            </p>
          </div>
          <StatusBadge tone="info">
            {data.payments.reduce((total, payment) => total + payment.receiptCount, 0)} archivos
          </StatusBadge>
        </header>
        {data.payments.length ? (
          <DataTable
            rows={data.payments}
            columns={paymentColumns}
            visibleColumnIds={new Set(paymentColumns.map((c) => c.id))}
            sort={{ columnId: 'period', direction: 'descending' }}
            onSort={() => {}}
            empty={
              <EmptyState title="Sin pagos" description="No existen pagos históricos asignados." />
            }
          />
        ) : (
          <EmptyState
            title="Sin pagos"
            description="No existen pagos históricos asignados a este socio."
          />
        )}
        <PaymentApplicationDetailModal
          payment={selectedPayment}
          onClose={() => setSelectedPayment(null)}
          viewer={'administrador'}
          memberId={memberId}
          onReceiptChanged={() => setRefreshKey((value) => value + 1)}
          onReversed={() => {
            setSelectedPayment(null);
            setRefreshKey((value) => value + 1);
          }}
          evidenceUrl={(id) =>
            data.payments.find((payment) => payment.id === id)?.sourceType === 'registered'
              ? manualAllocationEvidenceUrl(apiBase, id)
              : memberPaymentReceiptUrl(apiBase, memberId, id)
          }
          receiptUrl={(id) =>
            data.payments.find((payment) => payment.id === id)?.sourceType === 'registered'
              ? memberOfficialPaymentReceiptUrl(apiBase, memberId, id)
              : null
          }
        />
      </section>
    );
  return (
    <section className="member-detail-panel-stack">
      <CanonicalMetricGrid>
        <CanonicalMetricCard
          label="Obligaciones totales"
          value={data.totalObligations}
          icon={<ListChecks aria-hidden="true" />}
          tone="info"
          detail={`$${data.obligations} en cargos confirmados`}
        />
        <CanonicalMetricCard
          label="Obligaciones pagadas"
          value={data.paidObligations}
          icon={<CircleDollarSign aria-hidden="true" />}
          tone="success"
          detail={`$${data.appliedPayments} aplicado`}
        />
        <CanonicalMetricCard
          label="Obligaciones pendientes"
          value={data.pendingObligations}
          icon={<WalletCards aria-hidden="true" />}
          tone="warning"
          detail="Incluye obligaciones parcialmente pagadas"
        />
        <CanonicalMetricCard
          label="Saldo pendiente"
          value={`$${data.outstanding}`}
          icon={<CreditCard aria-hidden="true" />}
          tone="warning"
          detail="Obligaciones menos pagos aplicados"
        />
      </CanonicalMetricGrid>
      <section className="member-account-aliquot-card" aria-labelledby="aliquot-balance-title">
        <header className="member-account-aliquot-heading">
          <div>
            <span>DESGLOSE FINANCIERO</span>
            <h3 id="aliquot-balance-title">Estado por alícuota</h3>
          </div>
          <StatusBadge tone="neutral">
            {data.aliquots.length} {data.aliquots.length === 1 ? 'alícuota' : 'alícuotas'}
          </StatusBadge>
        </header>
        <div className="member-account-aliquot-table-wrap">
          <table className="ui-table member-account-aliquot-table">
            <thead>
              <tr>
                <th>Alícuota</th>
                <th>Total obligaciones</th>
                <th>Pagos aplicados</th>
                <th>Saldo pendiente</th>
                <th>Crédito a favor</th>
              </tr>
            </thead>
            <tbody>
              {data.aliquots.map((a) => (
                <tr key={a.slotNumber}>
                  <td>
                    <span className="member-account-aliquot-number">{a.slotNumber}</span>
                  </td>
                  <td>
                    <MoneyValue value={a.obligations} />
                  </td>
                  <td className="member-account-value--paid">
                    <MoneyValue value={a.appliedPayments} tone="positive" />
                  </td>
                  <td className="member-account-value--pending">
                    <MoneyValue value={a.outstanding} />
                  </td>
                  <td className="member-account-value--credit">
                    <MoneyValue value={a.credit} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </section>
  );
}
